import { of } from "rxjs";
import { Store } from "@ngrx/store";
import { inject, Injectable } from "@angular/core";
import { AppState } from "../../common/state/root";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { FeatureManagementActions } from "./feature-management.actions";
import { selectLoggedInUser } from "../../common/state/user/user.selectors";
import { FeatureManagementService } from "../service/feature-management.service";
import { mapFeatureRequest } from "../../common/models/helpers";
import { ToastType } from "../../common/models";
import { ToastService } from "../../common/services/toast/toast.service";

const INVALID_USER_ERROR = Error("Logged in user not valid");
const ERROR_RETRIEVING_FEATURES = Error("Error retrieving features");
const ERROR_ADDING_FEATURES = Error("Error adding feature");
const ERROR_UPDATING_FEATURES = Error("Error updating feature");
const ERROR_DELETING_FEATURES = Error("Error deleting feature");
const INVALID_USER_ERROR_RESPONSE = { error: INVALID_USER_ERROR };
const ERROR_RETRIEVING_RESPONSE = { error: ERROR_RETRIEVING_FEATURES };
const ERROR_ADDING_RESPONSE = { error: ERROR_ADDING_FEATURES };
const ERROR_UPDATING_RESPONSE = { error: ERROR_UPDATING_FEATURES };
const ERROR_DELETING_RESPONSE = { error: ERROR_DELETING_FEATURES };

@Injectable()
export class FeatureManagementEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<AppState>);
    private featureManagementService$ = inject(FeatureManagementService);
    private toastService = inject(ToastService);

    loadFeatures$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureManagementActions.loadFeatures),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([_, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureManagementActions.loadFeaturesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.featureManagementService$.getAllFeatures().pipe(
                map((response => {
                    return FeatureManagementActions.loadFeaturesSuccess({ features: response });
                })), catchError((error) => {
                    console.error(error);
                    return of(FeatureManagementActions.loadFeaturesFailure(ERROR_RETRIEVING_RESPONSE));
                })
            );
        })
    ));

    addFeature$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureManagementActions.addFeature),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureManagementActions.addFeatureFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.featureManagementService$.addFeature(mapFeatureRequest(action.feature)).pipe(
                map((response => {
                    return FeatureManagementActions.addFeatureSuccess({ feature: response });
                })), catchError((error) => {
                    console.error(error);
                    return of(FeatureManagementActions.addFeatureFailure(ERROR_ADDING_RESPONSE));
                })
            );
        })
    ));

    modifyFeature$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureManagementActions.modifyFeature),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureManagementActions.modifyFeatureFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.featureManagementService$.modifyFeature(mapFeatureRequest(action.feature)).pipe(
                map((response => {
                    return FeatureManagementActions.modifyFeatureSuccess({ feature: response });
                })), catchError((error) => {
                    console.error(error);
                    return of(FeatureManagementActions.modifyFeatureFailure(ERROR_UPDATING_RESPONSE));
                })
            );
        })
    ));

    deleteFeature$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureManagementActions.deleteFeature),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureManagementActions.deleteFeatureFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.featureManagementService$.deleteFeature(action.feature.id).pipe(
                map((response => {
                    return FeatureManagementActions.deleteFeatureSuccess({ feature: response });
                })), catchError((error: Error) => {
                    console.error(error);
                    this.toastService.showToast(
                        error?.message || 'Failed to delete feature',
                        ToastType.ERROR
                    );
                    return of(FeatureManagementActions.deleteFeatureFailure({ error }));
                })
            );
        })
    ));

}
